import { useMutation } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { hasPlatformAccess, login } from '@/features/auth/services/auth';
import type {
  LoginRequest,
  LoginResponse,
  LoginMFARequiredResponse,
  LoginSuccessResponse,
  FederatedLoginLocationState,
} from '@/features/auth/types/auth.types';
import { useAuthStore } from '@/features/auth/store/auth';
import { useAllowMeStore } from '@/shared/store/allowme';
import { getFederatedLoginRequired } from '@/shared/utils/apiError';
import i18n from '@/shared/i18n';

interface UseLoginOptions {
  onSuccess?: () => void;
  onError?: (error: Error) => void;
  /**
   * CA05 — chamado quando o backend recusa o login por senha com
   * FEDERATED_LOGIN_REQUIRED: o chamador decide a apresentação (Figma
   * "[Erro 01]": permanece no login com o alerta "SSO Identificado" e a ação
   * primária "Acessar via SSO"). Sem handler, navega para /sso (fallback).
   */
  onFederatedIdentified?: (state: FederatedLoginLocationState) => void;
  returnUrl?: string;
}

/**
 * Hook for user login with email and password
 * Handles login flow and transitions to MFA step on success
 *
 * @param options - Callbacks for success and error handling
 * @param options.returnUrl - Optional URL to redirect after successful login
 * @example
 * ```tsx
 * const { mutate: login, isPending } = useLogin({
 *   onSuccess: (email) => {
 *     console.log('Login successful for:', email);
 *   },
 *   onError: (error) => {
 *     console.error('Login failed:', error);
 *   },
 *   returnUrl: '/join?inviteId=123'
 * });
 *
 * login({ email: 'user@example.com', password: 'pass123' });
 * ```
 */
export function useLogin(options?: UseLoginOptions) {
  const navigate = useNavigate();
  const { loginMFARequired, loginCompleted, setError, setUsername } =
    useAuthStore();

  return useMutation<
    { data: LoginResponse; status: number },
    Error,
    LoginRequest
  >({
    mutationFn: async (payload) => {
      const { deviceCollectId } = useAllowMeStore.getState();
      return login({
        ...payload,
        ...(deviceCollectId ? { deviceCollectId } : {}),
      });
    },
    onSuccess: async (response, variables) => {
      const { data, status } = response;

      // Save username for continuity
      setUsername(variables.username);

      // MFA required: check if accessToken is empty
      if ('accessToken' in data && data.accessToken === '') {
        const mfaData = data as LoginMFARequiredResponse;
        loginMFARequired(variables.username, mfaData.sessionId);
        return;
      }

      if (status === 201) {
        const success = data as LoginSuccessResponse;
        if (!(await hasPlatformAccess(success.accessToken))) {
          setError(null);
          navigate('/login', {
            replace: true,
            state: {
              notice: 'access-not-granted',
              email: variables.username,
            },
          });
          return;
        }

        loginCompleted({
          accessToken: success.accessToken,
          expiresIn: success.expiresIn,
          tokenType: success.tokenType,
          scope: success.scope,
        });

        options?.onSuccess?.();

        const redirectTo = options?.returnUrl || '/';
        navigate(redirectTo, { replace: true });
        return;
      }
    },
    onError: (error, variables) => {
      // CA05 — usuário federado tentou entrar por senha: o acesso não é
      // concluído (mesmo com a senha correta) e o usuário é conduzido ao
      // fluxo corporativo sem reinformar o e-mail (via router state, nunca
      // na URL — PII).
      const federated = getFederatedLoginRequired(error);
      if (federated) {
        setError(null);
        const state: FederatedLoginLocationState = {
          email: variables.username,
          reason: 'federated-required',
          registrationId: federated.registrationId,
          authenticationUrl: federated.authenticationUrl,
        };
        if (options?.onFederatedIdentified) {
          options.onFederatedIdentified(state);
        } else {
          navigate('/sso', { state });
        }
        return;
      }

      // Internal: set error in store with standard message
      setError(i18n.t('Email or password incorrect'));

      // External: custom callback
      options?.onError?.(error);
    },
  });
}
